<template>
    <section class="section brackets-listing-page">
        <div class="container-fluid">
            <div class="columns">
                <div class="column">
                    <h1 class="title">{{ translate("admin_bracket_listing.title") }}</h1>
                </div>
            </div>

            <div v-if="!initDone" class="columns is-mobile is-centered">
                <LoadingSpinner2 class="has-added-margin" />
            </div>

            <div v-if="initDone" class="columns">
                <div class="column is-12">
                    <BracketsToolbar :event-id="eventId" />

                    <BracketsDisciplineStatusNotifications :event-id="eventId" notification-class="mb-3" />

                    <BracketsFilters />
                </div>
            </div>

            <div v-if="initDone" class="columns is-multiline">
                <div class="column is-12">
                    <!-- Stays visible in bulk mode even when the filters match nothing, so Cancel is always reachable. -->
                    <BracketsSelectionBar
                        v-if="store.filteredComplexBrackets.length > 0 || store.bulkSelectionEnabled"
                        @delete-selected="openBulkDeleteModal"
                    />

                    <BracketsList v-if="store.filteredComplexBrackets.length > 0" :event-id="eventId" />
                    <p v-else>
                        {{ translate("admin_bracket_listing.no_brackets") }}
                    </p>
                </div>
            </div>
        </div>

        <BracketsDefaultConfigurationModal :event-id="eventId" ref="bracketsDefaultConfigurationModal" />
        <AutoSeedBracketsModal :event-id="eventId" ref="autoSeedModal" />
        <MedalPlacesModal :event-id="eventId" ref="medalPlacesModal" />
        <BulkDeleteBracketsModal :event-id="eventId" ref="bulkDeleteBracketsModal" />
        <ShareBracketsModal ref="shareBracketsModal" />
    </section>
</template>

<script setup lang="ts">
    import { ref, nextTick, onMounted, onBeforeUnmount } from "vue";
    import { translate } from "../../Common/translations";
    import LoadingSpinner2 from "../../Common/LoadingSpinner2.vue";
    import AutoSeedBracketsModal from "./AutoSeedBracketsModal.vue";
    import BulkDeleteBracketsModal from "./BulkDeleteBracketsModal.vue";
    import MedalPlacesModal from "./MedalPlacesModal.vue";
    import ShareBracketsModal from "./ShareBracketsModal.vue";
    import BracketsDefaultConfigurationModal from "./BracketsDefaultConfigurationModal.vue";
    import { handleError } from "../../Common/errorHandler";
    import { useBracketsListingStore } from "./bracketsListingApp";
    import BracketsDisciplineStatusNotifications from "./BracketsDisciplineStatusNotifications.vue";
    import BracketsToolbar from "./BracketsToolbar.vue";
    import BracketsFilters from "./BracketsFilters.vue";
    import BracketsSelectionBar from "./BracketsSelectionBar.vue";
    import BracketsList from "./BracketsList.vue";
    import {
        eventBus,
        OPEN_AUTO_SEED_MODAL,
        OPEN_MEDAL_PLACES_MODAL,
        OPEN_BRACKET_DEFAULTS_MODAL,
        OPEN_SHARE_BRACKETS_MODAL,
    } from "./eventBus";
    import { useFeatureAnnouncement } from "../../Common/FeatureAnnouncements/useFeatureAnnouncement";

    const props = defineProps<{
        eventId: number;
        currentUserId?: number;
    }>();
    const store = useBracketsListingStore();
    store.eventId = props.eventId;

    const initDone = ref(false);

    const fetchComplexBrackets = () => {
        return store.refreshBracketsListing();
    };

    const bracketsDefaultConfigurationModal = ref<InstanceType<typeof BracketsDefaultConfigurationModal> | null>(null);

    const openBracketDefaultsModal = () => {
        bracketsDefaultConfigurationModal.value?.open();
    };

    const autoSeedModal = ref<InstanceType<typeof AutoSeedBracketsModal> | null>(null);

    const openAutoSeedModal = () => {
        autoSeedModal.value?.open();
    };

    const medalPlacesModal = ref<InstanceType<typeof MedalPlacesModal> | null>(null);

    const openMedalPlacesModal = () => {
        medalPlacesModal.value?.open();
    };

    const bulkDeleteBracketsModal = ref<InstanceType<typeof BulkDeleteBracketsModal> | null>(null);

    const openBulkDeleteModal = () => {
        bulkDeleteBracketsModal.value?.open();
    };

    const shareBracketsModal = ref<InstanceType<typeof ShareBracketsModal> | null>(null);

    const openShareBracketsModal = () => {
        shareBracketsModal.value?.open();
    };

    const openShareModalQueryParam = "openShareModal";

    const openShareModalFromDeepLink = (): boolean => {
        const params = new URLSearchParams(window.location.search);
        if (!params.has(openShareModalQueryParam)) {
            return false;
        }

        params.delete(openShareModalQueryParam);
        const query = params.toString();
        window.history.replaceState(null, "", window.location.pathname + (query ? `?${query}` : "") + window.location.hash);
        openShareBracketsModal();

        return true;
    };

    onMounted(() => {
        initDone.value = false;
        Promise.all([fetchComplexBrackets(), store.fetchDisciplinePackagesStatus(), store.fetchBracketsSharing()])
            .catch(handleError)
            .finally(() => {
                initDone.value = true;

                nextTick(() => {
                    const openedFromDeepLink = openShareModalFromDeepLink();

                    if (props.currentUserId && !openedFromDeepLink) {
                        useFeatureAnnouncement().showEligible(props.currentUserId, [
                            { key: "brackets_listing_share_button", element: "#brackets-share-button" },
                        ]);
                    }
                });
            });

        eventBus.$on(OPEN_AUTO_SEED_MODAL, openAutoSeedModal);
        eventBus.$on(OPEN_MEDAL_PLACES_MODAL, openMedalPlacesModal);
        eventBus.$on(OPEN_BRACKET_DEFAULTS_MODAL, openBracketDefaultsModal);
        eventBus.$on(OPEN_SHARE_BRACKETS_MODAL, openShareBracketsModal);
    });

    onBeforeUnmount(() => {
        eventBus.$off(OPEN_AUTO_SEED_MODAL, openAutoSeedModal);
        eventBus.$off(OPEN_MEDAL_PLACES_MODAL, openMedalPlacesModal);
        eventBus.$off(OPEN_BRACKET_DEFAULTS_MODAL, openBracketDefaultsModal);
        eventBus.$off(OPEN_SHARE_BRACKETS_MODAL, openShareBracketsModal);
    });
</script>

<style scoped></style>
